/**
 * The check behind a captured Message hand-off (KTD13, R20, R21).
 *
 * Each time the queue reads a captured hand-off (on mount, and each time the
 * page becomes visible again) it asks the server whether WhatsApp recorded an
 * outbound message to that person at or after the tap:
 *
 * - A return before the window has ended checks at once and then every 10
 *   seconds, for 2 minutes from that return. Nothing recorded by then marks
 *   the window as ended on the stored hand-off and falls back to the outcome
 *   prompt.
 * - A return after the window ended checks once; nothing recorded falls back
 *   to the outcome prompt straight away.
 * - A recorded message calls `onSent`.
 *
 * Before every check the stored hand-off is read again. When it has expired,
 * been finished, or been replaced by a newer tap, checking stops and nothing
 * opens. A failed check counts as nothing recorded.
 */

import { useEffect, useRef, useState } from "react";
import { checkWhatsappSent } from "@/features/realtor/services/realtor-crm-api.service";
import {
	isSameTap,
	markCheckWindowEnded,
	type PendingHandoff,
	readHandoff,
} from "./use-pending-handoff";

export const SENT_CHECK_INTERVAL_MS = 10_000;
export const SENT_CHECK_WINDOW_MS = 2 * 60_000;

export interface WhatsappSentCheck {
	/** A check is under way for `pending`. */
	checking: boolean;
	/** Nothing was recorded: ask with the outcome prompt instead. */
	fallback: boolean;
}

export function useWhatsappSentCheck(
	pending: PendingHandoff | null,
	onSent: (handoff: PendingHandoff) => void,
): WhatsappSentCheck {
	// Held as the tap they belong to, so a re-read of the same tap (a return
	// while the fallback prompt is open) keeps the prompt and what was typed.
	const [checkingTap, setCheckingTap] = useState<PendingHandoff | null>(null);
	const [fallbackTap, setFallbackTap] = useState<PendingHandoff | null>(null);
	const onSentRef = useRef(onSent);
	onSentRef.current = onSent;

	useEffect(() => {
		if (!pending?.captured) return;

		let cancelled = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const startedAt = Date.now();
		const once = pending.checkWindowEnded === true;
		const since = new Date(pending.createdAt).toISOString();
		const stillStored = (): boolean => isSameTap(readHandoff(), pending);

		const check = async (): Promise<void> => {
			if (!stillStored()) {
				setCheckingTap(null);
				return;
			}
			setCheckingTap(pending);
			const result = await checkWhatsappSent(pending.contactId, since);
			if (cancelled) return;
			if (result.success && result.data?.sent === true) {
				setCheckingTap(null);
				onSentRef.current(pending);
				return;
			}
			if (once || Date.now() - startedAt >= SENT_CHECK_WINDOW_MS) {
				setCheckingTap(null);
				if (!stillStored()) return;
				markCheckWindowEnded(pending);
				setFallbackTap(pending);
				return;
			}
			timer = setTimeout(() => void check(), SENT_CHECK_INTERVAL_MS);
		};

		void check();
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [pending]);

	return {
		checking: isSameTap(checkingTap, pending),
		fallback: isSameTap(fallbackTap, pending),
	};
}
